import {createContext, useCallback, useContext, useMemo} from 'react'

import {CENTER_MODAL_TYPE, useCenterModal} from '@defign/ui'

import {sendClickNlog} from '$utils/nlog'

import type {PropsWithChildren} from 'react'

interface ShowPopupParams {
    title?: string
    message?: string
    buttonText?: string
    nclkCode?: string
    onClick?: () => void
}

const Context = createContext<{showPopup: (params: ShowPopupParams) => void; closePopup: () => void} | undefined>(
    undefined,
)

const DEFAULT_BUTTON_TEXT = '확인'

export function PopupProvider({children}: PropsWithChildren<object>) {
    const {open, close} = useCenterModal()

    const handleOpenCenterModal = useCallback(
        (params: ShowPopupParams) => {
            const {title, message, buttonText, nclkCode, onClick} = params || {}
            open({
                type: CENTER_MODAL_TYPE.NOTICE,
                content: {
                    title,
                    description: message,
                },
                button: {
                    text: buttonText || DEFAULT_BUTTON_TEXT,
                    variant: 'tertiary',
                    onClick: () => {
                        if (nclkCode) {
                            sendClickNlog(nclkCode)
                        }
                        onClick?.()
                        close()
                    },
                },
            })
        },
        [open, close],
    )

    const value = useMemo(
        () => ({
            showPopup: handleOpenCenterModal,
            closePopup: close,
        }),
        [close, handleOpenCenterModal],
    )

    return <Context.Provider value={value}>{children}</Context.Provider>
}

export function usePopupContext() {
    const context = useContext(Context)

    if (context === undefined) {
        throw new Error('usePopupContext must be within PopupProvider')
    }

    return context
}
